import { useEffect, useRef, useState } from 'react';
import { styled, keyframes } from '@uhg-abyss/web/tools/styled';
import { AccountInfoBox } from './AccountInfoBox';
import { useAccounts } from '../context/AccountContext';
import SwitchAccountButton from './SwitchAccountButton';

export type SelectedAccountInfoBoxProps = {
    onSelect?: () => void;
};

const shimmer = keyframes({
    '0%': { borderColor: 'rgba(0, 123, 255, 0.35)', boxShadow: '0 0 8px 2px rgba(0, 123, 255, 0.35)' },
    '25%': { borderColor: 'rgba(120, 144, 169, 0.78)', boxShadow: '0 0 0 0 rgba(108, 194, 255, 0.78)' },
    '50%': { borderColor: 'rgba(0, 123, 255, 0.35)', boxShadow: '0 0 8px 2px rgba(0, 123, 255, 0.35)' },
    '75%': { borderColor: 'rgba(0, 123, 255, 0.78)', boxShadow: '0 0 0 0 rgba(0, 123, 255, 0.78)' },
    '100%': { borderColor: '$info1', boxShadow: '0 0 8px 2px rgba(0, 123, 255, 0)' },
});

const ClickableAccountInfoBox = styled(AccountInfoBox, {
    padding: '4px',
    margin: '4px',
    width: '320px',
    variants: {
        justSwitched: {
            true: { animation: `${shimmer} 2.3s ease-out` },
        },
    },
});

const SelectedAccountInfoBox = ({ onSelect }: SelectedAccountInfoBoxProps) => {
    const {
        accounts,
        selectedAccount,
        setSwitchAccountCallback,
    } = useAccounts();

    useEffect(() => {
        setSwitchAccountCallback(onSelect ?? (() => {}));
    }, [onSelect, setSwitchAccountCallback]);

    // Only shimmer on an actual account switch, not on the initial mount.
    const previousResourceIdRef = useRef(selectedAccount?.resourceId);
    const [justSwitched, setJustSwitched] = useState(false);

    useEffect(() => {
        const previousResourceId = previousResourceIdRef.current;
        previousResourceIdRef.current = selectedAccount?.resourceId;

        if (previousResourceId !== undefined && previousResourceId !== selectedAccount?.resourceId) {
            setJustSwitched(true);
            const timeout = setTimeout(() => setJustSwitched(false), 3000);
            return () => clearTimeout(timeout);
        }
    }, [selectedAccount?.resourceId]);

    return (
        selectedAccount &&
        <ClickableAccountInfoBox
            userId={selectedAccount.userId}
            domain={selectedAccount.resourceName}
            isLocked={selectedAccount.isLocked}
            justSwitched={justSwitched}
        >
            {accounts.length > 1 ? (
                <SwitchAccountButton />
            ) : null}
        </ClickableAccountInfoBox>
    );
}

export default SelectedAccountInfoBox;
